import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
} from '@angular/core';
import { CRMIntegrationsService } from '../../../services/crm-integrations.service';
import { forkJoin, Observable, of } from 'rxjs';
import {
  concatMap,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  tap,
} from 'rxjs/operators';
import { publicMethods } from '../../../globals';
import { FormControl } from '@angular/forms';
declare var $;

@Component({
    selector: 'wizard-crm-object-selector',
    templateUrl: './wizard-crm-object-selector.component.html',
    styleUrl: './wizard-crm-object-selector.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class WizardCrmObjectSelectorComponent {
  @Input() stepData: Record<string, any>;
  @Input() allSteps: Record<string, any>[];

  crmService = inject(CRMIntegrationsService);
  pubMethods = inject(publicMethods);
  cdr = inject(ChangeDetectorRef);

  LIST_OBJECTS = 'list_crm_object_types';
  CRM_OBJECT_TYPE = 'crm_object_type';

  crmType: string;
  existingState: Record<string, any> = null;
  selectedObjectType: Record<string, any> = null;
  existingSelection: Record<string, any> = {};
  pendingConfirmSelection: Record<string, any> = {};

  crmObjectList: Record<string, any>[] = [];
  filteredCRMObjects$: Observable<Record<string, any>[]>;
  objectSearchControl = new FormControl('');

  showConfirmModal = false;

  ngOnInit() {
    this.crmType = this.crmService.configCRMType();
    this.getStepActionContent();
  }

  setupFilteredList(): void {
    this.filteredCRMObjects$ = this.objectSearchControl.valueChanges.pipe(
      startWith(this.objectSearchControl.value || ''),
      debounceTime(220),
      distinctUntilChanged(),
      map(searchTerm => this.filterObjectsList(searchTerm.trim().toLowerCase()))
    );
  }

  filterObjectsList(searchTerm: string): Record<string, any>[] {
    if (!searchTerm) {
      return this.crmObjectList;
    }

    return this.crmObjectList?.filter(
      obj =>
        obj.label.toLowerCase().includes(searchTerm) ||
        obj.name.toLowerCase().includes(searchTerm)
    );
  }

  getStepActionContent(): void {
    forkJoin({
      stepActionContent: this.crmService.getCRMStepActionContent({
        stepType: this.stepData.stepType,
        actionType: this.LIST_OBJECTS,
      }),
      stepState: this.crmService.getCRMExistingState({
        actionType: this.CRM_OBJECT_TYPE,
        scope: 'configuration',
      }),
    })
      .pipe(
        tap(results => {
          if (results) {
            this.crmObjectList = results.stepActionContent;
            this.setupFilteredList();
            this.existingState = results.stepState;
            this.handleExistingState();
          }
        })
      )
      .subscribe();
  }

  handleExistingState(): void {
    if (this.crmType === 'dynamics' && !this.pubMethods.hasData(this.existingState)) {
      this.selectedObjectType = null;
      this.crmService.configCRMSelectedObjectId.set(null);
      return;
    }

    if (this.pubMethods.hasData(this.existingState)) {
      const lastObj = this.existingState.at(-1);

      const foundExisting = this.crmObjectList.find(
        obj => obj.name === lastObj.name
      );
      if (foundExisting) {
        this.crmObjectList = this.crmObjectList.sort((objA, objB) => {
          // foundExisting should be sorted first
          if (objA.name === foundExisting.name) return -1;
          if (objB.name === foundExisting.name) return 1;
          return 0;
        });
        this.selectedObjectType = foundExisting;
        this.existingSelection = foundExisting;
        // update objectId signal
        this.crmService.configCRMSelectedObjectId.update(() => lastObj.id);
        // check the step readiness
        this.crmService.getCRMConfigStepStatus(this.stepData.stepType);
      }
    }
  }

  renderCRMObjectIcons(objectName: string, size = 'sm', weight = 'r'): string {
    if (!objectName) return;

    switch (objectName) {
      case 'companies':
      case 'accounts':
        return `fa${weight} fa${size} fa-building`;

      case 'leads':
      case 'prospects':
      case 'contacts':
        return `fa${weight} fa${size} fa-user-plus`;

      case 'deals':
      case 'opportunities':
        return `fa${weight} fa${size} fa-handshake`;

      case 'cases':
      case 'tickets':
        return `fa${weight} fa${size} fa-tickets`;

      case 'campaigns':
        return `fa${weight} fa${size} fa-bullhorn`;

      default:
        return `fa${weight} fa${size} fa-objects-column`;
    }
  }

  confirmObjectTypeChange(newSelection: Record<string, any>): void {
    // fresh 1st time selection
    if (!this.pubMethods.hasData(this.existingState)) {
      this.selectedObjectType = newSelection;
      this.existingSelection = newSelection;
      this.postChoiceAndGetStatus();
      return;
    }

    // same as existing selection -> do nothing
    if (
      this.selectedObjectType &&
      this.selectedObjectType.name === newSelection.name
    ) {
      return;
    }

    this.pendingConfirmSelection = newSelection;

    this.showConfirmModal = true;
    this.cdr.detectChanges();
    setTimeout(() => {
      $('#confirmObjectTypeChange').modal();

      $('#confirmObjectTypeChange').one('hide.bs.modal', e => {
        // If pendingConfirmSelection is still set, it means it was dismissed without confirmation
        if (this.pendingConfirmSelection !== null) {
          this.handleModalDismiss(true); // revert
        } else {
          this.handleModalDismiss(false); // confirm change no revert
        }
      });
    }, 0);

    return;
  }

  handleModalDismiss(revertSelection: boolean): void {
    if (revertSelection) {
      // Revert selectedObjectType to the last confirmed state
      this.selectedObjectType = null;
      this.cdr.detectChanges();
      this.selectedObjectType = this.existingSelection;
      this.objectSearchControl.setValue(''); //reset search
    }
    this.pendingConfirmSelection = null;
    this.showConfirmModal = false;
    this.cdr.detectChanges();
  }

  postChoiceAndGetStatus(): void {
    let deleteObs$: Observable<any> = of(null);
    if (this.pubMethods.hasData(this.existingState)) {
      this.selectedObjectType = this.pendingConfirmSelection;
      this.existingSelection = this.pendingConfirmSelection;
      this.cdr.detectChanges();

      // hide the modal first
      $('#confirmObjectTypeChange').modal('hide');
      this.handleModalDismiss(false);

      //delete the existing
      const lastObjId = this.existingState.at(-1).id;
      if (lastObjId) {
        deleteObs$ = this.crmService.deleteCRMRecordObject(
          this.CRM_OBJECT_TYPE,
          lastObjId
        );
      }
    }

    const reqBody = {
      label: this.selectedObjectType.label,
      name: this.selectedObjectType.name,
    };

    deleteObs$
      .pipe(
        concatMap(() => {
          return this.crmService.postCRMStepActionJSON({
            postActionType: this.CRM_OBJECT_TYPE,
            params: reqBody,
          });
        }),
        concatMap(postResponse => {
          if (!postResponse.ok) {
            console.warn('error??', postResponse);
            return of(null);
          }
          const { id } = postResponse.body;
          //update objectId signal
          this.crmService.configCRMSelectedObjectId.update(() => {
            return id;
          });
          this.crmService.getCRMConfigStepStatus(this.stepData.stepType);
          return this.refreshExistingState();
        })
      )
      .subscribe();
  }

  // Keep existingState in step with the backend so a later selection replaces
  // the object just created instead of posting a duplicate alongside it.
  refreshExistingState(): Observable<any> {
    return this.crmService
      .getCRMExistingState({
        actionType: this.CRM_OBJECT_TYPE,
        scope: 'configuration',
      })
      .pipe(
        tap(stepState => {
          this.existingState = stepState;
          this.cdr.markForCheck();
        })
      );
  }
}
